import { getDeviceTimezone } from '@/utils/date';
import { HubCardType } from '@/types';
import { LowestPerformingCustomersHubCardData } from '@/experiences/sales-portal/domain/insights/blocs/types/insight';
import { partnerSupplier } from '@/utils/environment';
import { transformLowestPerformingCustomers } from '@/experiences/sales-portal/domain/insights/blocs/transformers/lowest-performing-customers-transformer';
import { useMemo } from 'react';
import { useSalesPortalGetHubCardDataQuery } from '@/experiences/sales-portal/domain/insights/blocs/documents/hub-card-data.generated';

export interface UseLowestPerformingCustomersLoaderResult {
  data: LowestPerformingCustomersHubCardData | null;
  error: Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Fetches Lowest Performing Customers hub card data
 *
 * @param {string} userUUID - The authenticated user's UUID; the query is skipped while
 *   it is unavailable.
 * @param {boolean} isGrossProfitDisabled - boolean flag returned by backend used to hide gross profit data
 *
 * @returns {@link UseLowestPerformingCustomersLoaderResult}
 *
 * @example
 * const { data, loading } = useLowestPerformingCustomersLoader(userUUID);
 */
export const useLowestPerformingCustomersLoader = (
  userUUID: string,
  isGrossProfitDisabled: boolean,
): UseLowestPerformingCustomersLoaderResult => {
  const { data, loading, error, refetch } = useSalesPortalGetHubCardDataQuery({
    fetchPolicy: 'cache-and-network',
    skip: !userUUID,
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      cardType: HubCardType.LowestPerformingCustomers,
      userTimezone: getDeviceTimezone(),
      userUUID: userUUID,
    },
  });

  const transformedData = useMemo(
    () =>
      transformLowestPerformingCustomers(
        data?.getHubCardData.card_data_json ?? [],
        isGrossProfitDisabled,
      ),
    [data, isGrossProfitDisabled],
  );

  return {
    data: transformedData,
    error: error ?? null,
    loading,
    refetch: () => {
      void refetch();
    },
  };
};
